import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { StreamChat, type Channel as StreamChannel } from 'stream-chat'
import { getStreamChatToken } from '@/lib/callables'
import { useAuth } from '@/hooks/use-auth'

const STREAM_API_KEY = (import.meta.env.VITE_STREAM_API_KEY as string | undefined) ?? ''

type StreamChatContextValue = {
  client: StreamChat | null
  streamId: string | null
  apiKey: string
  isReady: boolean
  isConnecting: boolean
  error: string | null
  reconnect: () => Promise<void>
}

const StreamChatContext = createContext<StreamChatContextValue | null>(null)

export function StreamChatProvider({ children }: { children: ReactNode }) {
  const { user, isGuest } = useAuth()
  const [client, setClient] = useState<StreamChat | null>(null)
  const [streamId, setStreamId] = useState<string | null>(null)
  const [isConnecting, setIsConnecting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const connect = useCallback(async () => {
    if (!STREAM_API_KEY) {
      setError(null)
      setClient(null)
      setStreamId(null)
      return
    }
    if (!user || isGuest) {
      setClient(null)
      setStreamId(null)
      return
    }

    setIsConnecting(true)
    setError(null)
    try {
      const { token, streamId: sid } = await getStreamChatToken()
      const chatClient = StreamChat.getInstance(STREAM_API_KEY)
      if (chatClient.userID === sid) {
        setClient(chatClient)
        setStreamId(sid)
        return
      }
      if (chatClient.userID) {
        await chatClient.disconnectUser()
      }
      // Match iOS: connect with Stream id only — server sets display name.
      await chatClient.connectUser({ id: sid }, token)
      setClient(chatClient)
      setStreamId(sid)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to connect to chat')
      setClient(null)
      setStreamId(null)
    } finally {
      setIsConnecting(false)
    }
  }, [user, isGuest])

  useEffect(() => {
    void connect()
    return () => {
      // Keep the singleton connected across remounts; disconnect on logout via effect below.
    }
  }, [connect])

  useEffect(() => {
    if (user && !isGuest) return
    const instance = STREAM_API_KEY ? StreamChat.getInstance(STREAM_API_KEY) : null
    if (instance?.userID) {
      void instance.disconnectUser()
    }
    setClient(null)
    setStreamId(null)
  }, [user, isGuest])

  const value = useMemo<StreamChatContextValue>(
    () => ({
      client,
      streamId,
      apiKey: STREAM_API_KEY,
      isReady: Boolean(client?.userID),
      isConnecting,
      error,
      reconnect: connect,
    }),
    [client, streamId, isConnecting, error, connect],
  )

  return <StreamChatContext.Provider value={value}>{children}</StreamChatContext.Provider>
}

export function useStreamChat() {
  const ctx = useContext(StreamChatContext)
  if (!ctx) throw new Error('useStreamChat must be used within StreamChatProvider')
  return ctx
}

// Re-export for consumers that import the hook module for channel typing.
export type { StreamChannel }
